import { IconCalendarEvent, IconMapPin } from '@tabler/icons-react';
import { Box } from '../../atoms/Box';
import { Group } from '../../atoms/Group';
import { Stack } from '../../atoms/Stack';
import { Text } from '../../atoms/Text';
import { Divider } from '../../atoms/Divider';
import { Badge } from '../../atoms/Badge';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import { splitNameInitial } from './splitNameInitial';
import { DEFAULT_ORG_TIMEZONE, formatOrgDate } from '../../utils/formatOrgDate';

export interface ProcurementMobileListRowProps extends CoreProps {
  partyName: string;
  procType: 'SALES' | 'PURCHASE' | string;
  amount?: number | null;
  /** Raw ISO timestamp — formatted with `timeZone` when `dateLabel` is omitted. */
  createdAt?: string | null;
  /** Pre-formatted date from `useDate().format(...)` — preferred when set. */
  dateLabel?: string | null;
  /** IANA timezone for date display (org timezone). Defaults to Asia/Kolkata. */
  timeZone?: string;
  productSummary?: string | null;
  moreProductCount?: number;
  address?: string | null;
  imageUrl?: string | null;
  showDivider?: boolean;
  onClick?: () => void;
}

function formatCurrency(amount: number): string {
  return `₹${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export function ProcurementMobileListRow({
  partyName,
  procType,
  amount,
  createdAt,
  dateLabel,
  timeZone = DEFAULT_ORG_TIMEZONE,
  productSummary,
  moreProductCount = 0,
  address,
  imageUrl,
  showDivider = true,
  onClick,
  testId,
  view = 'all',
}: ProcurementMobileListRowProps) {
  const shouldRender = useShouldRender(view);
  if (!shouldRender) return null;

  const initials = splitNameInitial(partyName);
  const isPurchase = String(procType).toUpperCase() === 'PURCHASE';
  const typeLabel = isPurchase ? 'Purchase' : 'Sales';
  const displayDate = dateLabel || (createdAt ? formatOrgDate(createdAt, timeZone, 'date') : '');

  return (
    <>
      <Group
        gap="sm"
        wrap="nowrap"
        align="flex-start"
        px="md"
        py="sm"
        data-testid={testId}
        style={onClick ? { cursor: 'pointer' } : undefined}
        onClick={onClick}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              objectFit: 'cover',
              flexShrink: 0,
              alignSelf: 'center',
            }}
          />
        ) : (
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--mantine-color-gray-2)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              alignSelf: 'center',
            }}
          >
            <Text size="sm" fw={600} c="brand">
              {initials}
            </Text>
          </Box>
        )}

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="md" lineClamp={1} c="dark">
            {partyName}
          </Text>
          {productSummary ? (
            <Text size="xs" c="dark" lineClamp={1}>
              {productSummary}
              {moreProductCount > 0 ? (
                <>
                  {' '}
                  and{' '}
                  <Text span size="sm" fw={700} c="brand">
                    {moreProductCount}+more
                  </Text>
                </>
              ) : null}
            </Text>
          ) : null}
          {address ? (
            <Group gap={4} wrap="nowrap">
              <IconMapPin size={10} color="var(--mantine-color-dark-6)" />
              <Text size="xs" c="dark" lineClamp={1}>
                {address}
              </Text>
            </Group>
          ) : null}
        </Stack>

        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
          <Badge
            size="sm"
            radius="md"
            color={isPurchase ? 'red' : 'green'}
            variant="light"
          >
            {typeLabel}
          </Badge>
          {amount != null ? (
            <Text size="xs" fw={700} c="red">
              {formatCurrency(Number(amount))}
            </Text>
          ) : null}
          {displayDate ? (
            <Group gap={4} wrap="nowrap">
              <IconCalendarEvent size={10} color="var(--mantine-color-gray-6)" />
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {displayDate}
              </Text>
            </Group>
          ) : null}
        </Stack>
      </Group>
      {showDivider ? <Divider color="gray.2" /> : null}
    </>
  );
}
